import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg, useApi, todayISO, monthISO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Loader, Empty, Table, Badge, StatCard, ExportButtons, useChildren, ChildPicker } from "../components/kit";
import { QrAttendance } from "../components/QrAttendance";

const STATUS = [["P", "Present", "bg-emerald-500 text-white"], ["A", "Absent", "bg-rose-500 text-white"], ["L", "Late", "bg-amber-500 text-black"]];

function ClassSelect({ classes, value, onChange, testid }) {
  return (
    <select className="field sm:!w-56" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
    </select>
  );
}

function MarkTab({ classes, t }) {
  const [cls, setCls] = useState(classes[0]?.id || "");
  const [date, setDate] = useState(todayISO());
  const { data, loading, reload } = useApi(cls ? `/attendance?class_id=${cls}&date=${date}` : null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRows(data?.students || []);
  }, [data]);
  const setS = (id, s) => setRows(rows.map((r) => (r.id === id ? { ...r, status: s } : r)));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/attendance", { class_id: cls, date, records: rows.map((x) => ({ student_id: x.id, status: x.status })) });
      toast.success(`Saved · ${r.data.present}/${r.data.total} present`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const counts = STATUS.map(([k]) => rows.filter((r) => r.status === k).length);
  return (
    <GlassCard className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <ClassSelect classes={classes} value={cls} onChange={setCls} testid="mark-class-select" />
        <input type="date" className="field sm:!w-48" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="mark-date-input" />
        <div className="flex gap-2 sm:ml-auto text-xs">
          {STATUS.map(([k, l], i) => <Badge key={k} status={k}>{t(l)} {counts[i]}</Badge>)}
        </div>
      </div>
      {data?.marked && <p className="mb-4 text-xs text-zinc-400" data-testid="attendance-marked-note">Already marked by {data.marked_by} — saving will update it.</p>}
      {loading ? <Loader /> : !rows.length ? <Empty hint="No students in this class." /> : (
        <>
          <div className="space-y-2" data-testid="attendance-mark-list">
            {rows.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                className="flex items-center justify-between gap-3 rounded-2xl bg-white/[0.03] border border-white/5 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white/5 text-xs font-semibold text-zinc-300">{r.roll_no}</span>
                  <span className="truncate text-sm font-semibold text-white">{r.name}</span>
                </div>
                <div className="flex gap-1 rounded-full bg-white/5 p-1">
                  {STATUS.map(([k, l, cls2]) => (
                    <button key={k} onClick={() => setS(r.id, k)} data-testid={`att-${r.id}-${k}`}
                      className={`rounded-full px-3 py-1 text-xs font-bold transition-colors duration-200 ${r.status === k ? cls2 : "text-zinc-400 hover:text-white"}`}>
                      <span className="hidden sm:inline">{t(l)}</span><span className="sm:hidden">{k}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setRows(rows.map((r) => ({ ...r, status: "P" })))} data-testid="mark-all-present-btn"><CheckCircle size={16} /> All present</button>
            <button className="btn-primary" disabled={busy} onClick={save} data-testid="save-attendance-btn"><FloppyDisk size={16} /> {t("Save attendance")}</button>
          </div>
        </>
      )}
    </GlassCard>
  );
}

function DailyTab({ t }) {
  const [date, setDate] = useState(todayISO());
  const { data, loading } = useApi(`/attendance/overview?date=${date}`);
  return (
    <GlassCard className="p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <input type="date" className="field !w-48" value={date} onChange={(e) => setDate(e.target.value)} data-testid="overview-date-input" />
        <div className="font-display text-2xl text-white" data-testid="overview-percent">{data?.percent != null ? `${data.percent}%` : "—"} <span className="text-sm text-zinc-400">school-wide</span></div>
      </div>
      {loading ? <Loader /> : (
        <Table testid="daily-overview-table">
          <thead><tr><th>{t("Class")}</th><th>Strength</th><th>{t("Present")}</th><th>{t("Late")}</th><th>{t("Absent")}</th><th>%</th><th>{t("Status")}</th></tr></thead>
          <tbody>
            {data?.classes.map((c) => (
              <tr key={c.class_id}>
                <td className="font-semibold text-white">{c.class_name}</td><td>{c.strength}</td><td className="text-emerald-300">{c.present}</td>
                <td className="text-amber-300">{c.late}</td><td className="text-rose-300">{c.absent}</td><td>{c.percent != null ? `${c.percent}%` : "—"}</td>
                <td><Badge status={c.marked ? "paid" : "pending"}>{c.marked ? "Marked" : "Not marked"}</Badge></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </GlassCard>
  );
}

function MonthlyTab({ classes, t }) {
  const [cls, setCls] = useState(classes[0]?.id || "");
  const [month, setMonth] = useState(monthISO());
  const { data, loading } = useApi(cls ? `/attendance/monthly?class_id=${cls}&month=${month}` : null);
  return (
    <GlassCard className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <ClassSelect classes={classes} value={cls} onChange={setCls} testid="monthly-class-select" />
        <input type="month" className="field sm:!w-48" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-month-input" />
      </div>
      {loading ? <Loader /> : !data?.dates.length ? <Empty hint="No attendance marked this month." /> : (
        <div className="overflow-x-auto">
          <table className="tbl" data-testid="monthly-grid">
            <thead><tr><th className="sticky left-0 bg-[#0a0a12]">{t("Name")}</th>{data.dates.map((d) => <th key={d} className="!px-1.5 text-center">{d.slice(8)}</th>)}<th>%</th></tr></thead>
            <tbody>
              {data.students.map((s) => (
                <tr key={s.id}>
                  <td className="sticky left-0 bg-[#0a0a12] font-semibold text-white">{s.name}</td>
                  {data.dates.map((d) => {
                    const v = s.days[d];
                    return <td key={d} className="!px-1.5 text-center"><span className={`inline-block h-2.5 w-2.5 rounded-full ${v === "P" ? "bg-emerald-400" : v === "A" ? "bg-rose-500" : v === "L" ? "bg-amber-400" : "bg-white/10"}`} title={v} /></td>;
                  })}
                  <td className={s.percent >= 75 ? "text-emerald-300" : "text-rose-300"}>{s.percent ?? "—"}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
}

function FamilyAttendance({ t }) {
  const { kids, sel, setSel } = useChildren();
  const [month, setMonth] = useState(monthISO());
  const { data, loading } = useApi(sel ? `/attendance/student/${sel}?month=${month}` : null);
  return (
    <div>
      <PageHeader eyebrow={data?.student?.name} title={t("Attendance")} actions={<><ChildPicker kids={kids} sel={sel} setSel={setSel} />
        <input type="month" className="field !w-48 !rounded-full" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="family-month-input" /></>} />
      {loading || !data ? <Loader /> : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard label={t("Percentage")} value={data.percent != null ? `${data.percent}%` : "—"} tone="green" testid="family-att-percent" />
          <StatCard label={t("Present")} value={data.present} tone="blue" testid="family-att-present" delay={0.05} />
          <StatCard label={t("Absent")} value={data.absent} tone="rose" testid="family-att-absent" delay={0.1} />
          <GlassCard className="p-6 md:col-span-3">
            {!data.days.length ? <Empty /> : (
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-3" data-testid="family-att-days">
                {data.days.map((d) => (
                  <div key={d.date} className="rounded-2xl bg-white/[0.03] border border-white/5 p-3 text-center">
                    <div className="text-xs text-zinc-500">{new Date(d.date).toLocaleDateString("en-IN", { weekday: "short" })}</div>
                    <div className="font-display text-xl text-white">{d.date.slice(8)}</div>
                    <Badge status={d.status}>{t(STATUS.find((s) => s[0] === d.status)[1])}</Badge>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      )}
    </div>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const { t } = useT();
  const { data: classes } = useApi(user.role === "parent" ? null : "/classes");
  if (user.role === "parent") return <FamilyAttendance t={t} />;
  return (
    <div>
      <PageHeader eyebrow="Daily register" title={t("Attendance")} subtitle="Absent students' parents get a WhatsApp-ready alert automatically."
        actions={user.role === "admin" && <ExportButtons entity="attendance" />} />
      {!classes ? <Loader /> : !classes.length ? <GlassCard><Empty hint="Create a class first." /></GlassCard> : (
        <Tabs defaultValue="mark">
          <TabsList className="mb-6 bg-white/5 border border-white/10 rounded-full p-1 h-auto">
            <TabsTrigger value="mark" className="rounded-full px-4 py-1.5 data-[state=active]:bg-blue-500 data-[state=active]:text-white" data-testid="tab-mark">{t("Mark attendance")}</TabsTrigger>
            <TabsTrigger value="scan" className="rounded-full px-4 py-1.5 data-[state=active]:bg-blue-500 data-[state=active]:text-white" data-testid="tab-scan">Scan QR</TabsTrigger>
            <TabsTrigger value="daily" className="rounded-full px-4 py-1.5 data-[state=active]:bg-blue-500 data-[state=active]:text-white" data-testid="tab-daily">{t("Daily")}</TabsTrigger>
            <TabsTrigger value="monthly" className="rounded-full px-4 py-1.5 data-[state=active]:bg-blue-500 data-[state=active]:text-white" data-testid="tab-monthly">{t("Monthly")}</TabsTrigger>
          </TabsList>
          <TabsContent value="mark"><MarkTab classes={classes} t={t} /></TabsContent>
          <TabsContent value="scan"><QrAttendance classes={classes} /></TabsContent>
          <TabsContent value="daily"><DailyTab t={t} /></TabsContent>
          <TabsContent value="monthly"><MonthlyTab classes={classes} t={t} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
